import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnInit,
  TrackByFunction,
  ViewChild
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatOption } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSelect, MatSelectChange } from "@angular/material/select";
import { CaseType, Override } from "@vp/models";
import { BehaviorSubject } from "rxjs";

export interface OverrideFormDialogData {
  allCaseTypes: CaseType[];
  override: Override;
  mode: "edit" | "create";
}

@Component({
  selector: "vp-add-form",
  standalone: false,
  templateUrl: "./add-form.component.html",
  styleUrls: ["./add-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFormComponent implements OnInit {
  @ViewChild("select") select!: MatSelect;

  form!: UntypedFormGroup;
  originalClose;
  allSelected = false;

  private readonly selectedCaseTypes$$ = new BehaviorSubject<string[]>([]);

  selectedCaseTypes$ = this.selectedCaseTypes$$.asObservable();

  constructor(
    private readonly dialogRef: MatDialogRef<AddFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OverrideFormDialogData,
    private readonly fb: UntypedFormBuilder
  ) {
    // Add confirm close logic to dialog to centralize
    this.originalClose = this.dialogRef.close;
    this.dialogRef.close = this.confirmClose.bind(this);
  }

  @HostListener("window:keyup.esc") onKeyUp() {
    this.confirmClose({ save: false });
  }

  @HostListener("window:beforeunload", ["$event"]) unloadHandler(event: Event) {
    event.returnValue = false;
  }

  ngOnInit(): void {
    this.form = this.getForm(this.data.override);
    if (this.data.mode === "create" && this.form.get("active")?.value === null) {
      this.form.get("active")?.setValue(true);
    }
    if (this.data.mode === "edit") {
      this.form.get("caseTypeId")?.disable();
      this.selectedCaseTypes$$.next([this.form.get("caseTypeId")?.value]);
    }
  }

  selectAll(event: { checked: boolean }) {
    this.allSelected = event.checked;
    if (this.allSelected) {
      this.selectedCaseTypes$$.next(this.data.allCaseTypes.map(ct => ct.caseTypeId));
      this.select.options.forEach((op: MatOption) => op.select());
    } else {
      this.selectedCaseTypes$$.next([]);
      this.select.options.forEach((item: MatOption) => item.deselect());
    }
  }

  caseTypeChanged(event: MatSelectChange) {
    this.selectedCaseTypes$$.next(event.value);
    this.allSelected = this.selectedCaseTypes$$.value.length === this.data.allCaseTypes.length;
  }

  // Satisfies template-use-track-by-function
  trackByFn: TrackByFunction<CaseType> = (_index: number, item: CaseType) => item.caseTypeId;

  confirmClose(result: { save?: boolean }) {
    if (result.save && this.form.valid) {
      const formValue = this.form.getRawValue();
      const overrides: Override[] = formValue.caseTypeId.map((caseTypeId: string) => ({
        caseTypeId,
        fee: formValue.fee,
        active: formValue.active
      }));
      this.originalClose.bind(this.dialogRef)(overrides);
    } else {
      if (this.form.pristine || this.form.invalid) {
        this.originalClose.bind(this.dialogRef)();
      } else {
        if (confirm("Are you sure you want to cancel and potentially lose data?")) {
          this.originalClose.bind(this.dialogRef)();
        }
      }
    }
  }

  private getForm(override: Override) {
    return this.fb.group({
      caseTypeId: [[override.caseTypeId ?? null]],
      active: [override.active ?? null],
      fee: [override.fee ?? null]
    });
  }
}
